import React, { useState, useEffect, useCallback } from 'react';
import { useSelectedBrand } from '../hooks/useApi';
import LoadingSpinner from '../components/LoadingSpinner';
import apiService from '../services/api';

const Audience = () => {
  const { selectedBrandId, setSelectedBrandId, brands: brandOptions, brandsLoading } = useSelectedBrand();
  const [stats, setStats] = useState(null);
  const [connections, setConnections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [reclassifying, setReclassifying] = useState(false);
  const [filter, setFilter] = useState({ is_icp: '', segment: '', search: '' });

  const fetchData = useCallback(async () => {
    if (!selectedBrandId) return;
    try {
      setLoading(true);
      setError(null);
      const [statsRes, connRes] = await Promise.all([
        apiService.getConnectionStats(selectedBrandId),
        apiService.getConnections({
          brand_id: selectedBrandId,
          ...(filter.is_icp !== '' && { is_icp: filter.is_icp }),
          ...(filter.segment && { segment: filter.segment }),
          ...(filter.search && { search: filter.search }),
        }),
      ]);
      setStats(statsRes);
      setConnections(connRes);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedBrandId, filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('brand_id', selectedBrandId);
      const result = await apiService.importConnections(formData);
      setImportResult(result);
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleReclassify = async () => {
    setReclassifying(true);
    try {
      await apiService.reclassifyConnections(selectedBrandId);
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setReclassifying(false);
    }
  };

  if (brandsLoading) return <LoadingSpinner />;

  const segmentColors = {
    roofing: 'bg-orange-100 text-orange-700',
    hvac: 'bg-blue-100 text-blue-700',
    plumbing: 'bg-cyan-100 text-cyan-700',
    smb_owner: 'bg-green-100 text-green-700',
    home_services: 'bg-amber-100 text-amber-700',
    irrelevant: 'bg-gray-100 text-gray-500',
    unclassified: 'bg-gray-50 text-gray-400',
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audience</h1>
          <p className="text-sm text-gray-500 mt-1">LinkedIn connections & ICP tracking</p>
        </div>
        <div className="flex items-center gap-3">
          {brandOptions.length > 1 && (
            <select
              value={selectedBrandId || ''}
              onChange={e => setSelectedBrandId(Number(e.target.value))}
              className="border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500"
            >
              {brandOptions.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <label className="px-4 py-2 bg-accent-600 text-white rounded-xl text-sm font-medium hover:bg-accent-700 cursor-pointer transition-colors">
            {importing ? <i className="fas fa-spinner fa-spin mr-1"></i> : <i className="fas fa-upload mr-1"></i>}
            Import CSV
            <input type="file" accept=".csv" onChange={handleImport} className="hidden" />
          </label>
          <button
            onClick={handleReclassify}
            disabled={reclassifying}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            {reclassifying ? <i className="fas fa-spinner fa-spin mr-1"></i> : <i className="fas fa-sync mr-1"></i>}
            Re-classify
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">{error}</div>
      )}

      {importResult && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-green-700 text-sm">
          Imported: {importResult.created} new, {importResult.updated} updated, {importResult.skipped} skipped
          <button onClick={() => setImportResult(null)} className="ml-2 underline">dismiss</button>
        </div>
      )}

      {loading ? <LoadingSpinner /> : (
        <>
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <div className="text-sm text-gray-500 mb-1">Total Connections</div>
                <div className="text-2xl font-bold text-gray-900">{stats.total.toLocaleString()}</div>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <div className="text-sm text-gray-500 mb-1">ICP Match</div>
                <div className="text-2xl font-bold text-green-600">{stats.icp_count}</div>
                <div className="text-xs text-gray-400">{stats.icp_pct}% of total</div>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <div className="text-sm text-gray-500 mb-1">Target ICP</div>
                <div className="text-2xl font-bold text-accent-600">30%</div>
                <div className="text-xs text-gray-400">by June</div>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <div className="text-sm text-gray-500 mb-1">Segments</div>
                <div className="text-2xl font-bold text-gray-900">{Object.keys(stats.segments).length}</div>
              </div>
            </div>
          )}

          {/* Segments Breakdown */}
          {stats && Object.keys(stats.segments).length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Composition</h3>
              <div className="space-y-2">
                {Object.entries(stats.segments)
                  .sort((a, b) => b[1] - a[1])
                  .map(([seg, count]) => {
                    const pct = stats.total > 0 ? Math.round(count / stats.total * 100) : 0;
                    return (
                      <div key={seg} className="flex items-center gap-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${segmentColors[seg] || 'bg-gray-100 text-gray-600'}`}>
                          {seg}
                        </span>
                        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-accent-400 rounded-full"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-600 w-20 text-right">{count} ({pct}%)</span>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Recent Connections */}
          {stats?.recent_connections?.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Recent Connections</h3>
              <div className="space-y-2">
                {stats.recent_connections.map((c, i) => (
                  <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${c.is_icp ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-800">{c.name}</span>
                      {c.company && <span className="text-sm text-gray-400 ml-2">- {c.company}</span>}
                    </div>
                    {c.is_icp && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">ICP</span>
                    )}
                    {c.segment && (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${segmentColors[c.segment] || 'bg-gray-100 text-gray-600'}`}>
                        {c.segment}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Connections List */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">All Connections</h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Search..."
                  value={filter.search}
                  onChange={e => setFilter(f => ({ ...f, search: e.target.value }))}
                  className="border border-gray-300 rounded-xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500 w-40"
                />
                <select
                  value={filter.is_icp}
                  onChange={e => setFilter(f => ({ ...f, is_icp: e.target.value }))}
                  className="border border-gray-300 rounded-xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500"
                >
                  <option value="">All</option>
                  <option value="true">ICP only</option>
                  <option value="false">Non-ICP</option>
                </select>
              </div>
            </div>

            {connections?.results?.length === 0 && connections?.count === 0 ? (
              <div className="text-center text-gray-400 py-8">
                <i className="fas fa-users text-3xl mb-2"></i>
                <p>No connections yet. Import a LinkedIn CSV to get started.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {(connections?.results || []).map(c => (
                  <div key={c.id} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${c.is_icp ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800">{c.name}</div>
                      <div className="text-xs text-gray-400 truncate">{c.headline || c.title}</div>
                    </div>
                    <span className="text-xs text-gray-400 hidden md:inline">{c.company}</span>
                    {c.segment && (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${segmentColors[c.segment] || 'bg-gray-100 text-gray-600'}`}>
                        {c.segment}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Audience;
